/**
 * An editor's "Send to owner for review" (FRONT-2940, FRONT-3161, ADR-0081
 * decision 6): in their draft's coding window it stands where the owner has
 * Save App, and on their own row of the drafts list. It rings the owner's
 * bell; nothing an editor does moves the app.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { CircleNotch, PaperPlaneTilt } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { Textarea } from '~/components/ui/textarea';
import { cn } from '~/lib/utils';
import { useAskOwnerToAccept, type AskAnswer } from '~/hooks/queries/use-app-drafts';
import type { CodingAppView } from '~/features/coding/coding-events';

export const ASK_BUSY = 'Available once EDDY finishes the current run';
export const ASK_PENDING = 'Sending…';
export const ASK_LABEL = 'Send to owner for review';
/** FRONT-3159: the server's own cap on the note. */
export const NOTE_MAX = 500;

export type PublishControl = 'save' | 'ask' | 'none';

/** Which control the coding header shows where the owner has Save App. */
export function publishControlFor(session: { app?: Pick<CodingAppView, 'viewerIsEditor'> | null; appUnavailable?: string | null }): PublishControl {
  if (session.appUnavailable) return 'none';
  return session.app?.viewerIsEditor ? 'ask' : 'save';
}

/** Why the ask cannot be pressed right now; null when it can. One decision for disabled and its explanation. */
export function askGate(input: { runBusy: boolean; pending: boolean }): string | null {
  if (input.pending) return ASK_PENDING;
  if (input.runBusy) return ASK_BUSY;
  return null;
}

export function askAnswerMessage(answer: AskAnswer): string {
  return answer.alreadyAsked
    ? `Already sent to ${answer.owner}. They'll see it in their notifications.`
    : `Sent to ${answer.owner} for review.`;
}

export function askHint(ownerName: string): string {
  return `${ownerName.charAt(0).toUpperCase()}${ownerName.slice(1)} reviews your changes and adds them to the app`;
}

export function AskOwnerToAcceptButton({
  slug,
  ownerName,
  runBusy,
  size,
  variant,
  className,
}: {
  slug: string;
  ownerName: string;
  runBusy: boolean;
  size?: 'sm';
  /** The drafts list's row keeps a quiet outline; elsewhere it is the primary action. */
  variant?: 'outline';
  className?: string;
}) {
  const ask = useAskOwnerToAccept(slug);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const blockedBy = askGate({ runBusy, pending: ask.isPending });
  const send = () =>
    ask.mutate(note, {
      onSuccess: (answer) => {
        setOpen(false);
        setNote('');
        toast.success(askAnswerMessage(answer));
      },
      onError: (e: Error) => toast.error(e.message || `Could not send to ${ownerName}`),
    });
  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        disabled={blockedBy !== null}
        tooltip={blockedBy ?? askHint(ownerName)}
        aria-label={ASK_LABEL}
        data-testid="ask-owner-to-accept"
        className={cn('shrink-0 gap-1.5', className)}
        onClick={() => setOpen(true)}
      >
        {ask.isPending ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} />}
        {ASK_LABEL}
      </Button>
      {/* FRONT-3159: an optional note, shown to the owner as the editor's own words. */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="send-for-review-dialog">
          <DialogHeader>
            <DialogTitle>Send your changes to {ownerName}</DialogTitle>
            <DialogDescription>Add a note if you like. {ownerName} sees it next to your changes, in your words.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={NOTE_MAX}
              rows={4}
              aria-label="Note for the owner"
              placeholder="What did you change, and why?"
              data-testid="send-for-review-note"
            />
            <p className="text-right text-xs text-muted-foreground tabular-nums">
              {note.length}/{NOTE_MAX}
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Not now
            </Button>
            <Button
              type="button"
              disabled={blockedBy !== null}
              tooltip={blockedBy ?? undefined}
              data-testid="send-for-review"
              className="gap-1.5"
              onClick={send}
            >
              {ask.isPending ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
